/**
 * ApiConfig / Data Hub — Zentrale Konfiguration aller Datenverbindungen und Sync-Jobs.
 *
 * Features:
 * - Verbindungsstatus für PostgreSQL, Odoo, OMS, Legacy Dev
 * - Sync-Jobs starten, testen und überwachen (Sync-Log)
 * - API-Keys verwalten (CRUD) und Zugriffs-Log
 * - SMTP- und Anthropic-API-Konfiguration
 * - Cronjob-Verwaltung (aktivieren, manuell ausführen)
 * - Initialer Daten-Import und Lücken-Analyse
 *
 * @page /data-hub
 * @roles admin
 * @i18n dataHub, common
 * @endpoints GET/POST /api/data-hub/*, GET/POST /api/system/*, GET/POST/PUT/DELETE /api/admin/api-keys, GET /api/admin/api-access-log, GET/POST/PUT /api/admin/cronjobs/*, GET/POST /api/odoo/config
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import useTabParam from '../hooks/useTabParam';
import { RefreshCw, Package, ClipboardList, CheckCircle, XCircle, Clock, Download, Settings, AlertTriangle } from 'lucide-react';
import { PageHeader, ToggleSwitch } from '../components/ui';
import { useLanguage } from '../context/LanguageContext';

function api(path, opts = {}) {
  const hasBody = opts.body != null;
  const headers = hasBody ? { 'Content-Type': 'application/json' } : {};
  return fetch(path, {
    ...opts,
    headers: { ...headers, ...opts.headers },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
  }).then(async (r) => {
    const data = await r.json();
    if (!r.ok) throw new Error(data.message || `HTTP ${r.status}`);
    return data;
  });
}

/* ─── Helpers ─── */
function Dot({ status }) {
  const color =
    status === 'online' || status === 'connected' || status === 'verbunden' || status === true
      ? 'bg-green-500'
      : status === 'fehler' || status === false
      ? 'bg-red-500'
      : status === 'nicht_konfiguriert' || status === 'konfiguriert'
      ? 'bg-amber-500'
      : 'bg-gray-500';
  return <span className={`w-2 h-2 rounded-full ${color} flex-shrink-0`} />;
}

function timeAgo(ts, t) {
  if (!ts) return '–';
  const d = new Date(ts);
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (t) {
    if (s < 60) return t('dataHub.timeAgo.seconds', { s });
    if (s < 3600) return t('dataHub.timeAgo.minutes', { m: Math.floor(s / 60) });
    if (s < 86400) return t('dataHub.timeAgo.hours', { h: Math.floor(s / 3600) });
  } else {
    if (s < 60) return `vor ${s}s`;
    if (s < 3600) return `vor ${Math.floor(s / 60)} Min`;
    if (s < 86400) return `vor ${Math.floor(s / 3600)} Std`;
  }
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

function EyeIcon({ open }) {
  if (open) return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>;
  return <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>;
}

function InfoRow({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-line">
      <span className="text-xs text-[var(--text-secondary)]">{label}</span>
      <span className={`text-xs text-content-secondary ${mono ? 'font-mono' : ''}`}>{value}</span>
    </div>
  );
}

function fmt(n) { return n != null ? Number(n).toLocaleString('de-DE') : '–'; }

/* ─── Connection Card ─── */
function ConnCard({ title, icon, status, statusLabel, details, onTest, testing, color, badges, t }) {
  return (
    <div className="bg-surface-card border border-line rounded-card p-4 shadow-card min-w-0">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <span className="flex-shrink-0 text-[var(--text-secondary)]">
            {icon}
          </span>
          <div className="min-w-0">
            <div className="text-xs font-semibold text-content truncate">{title}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <Dot status={status} />
              <span className="text-[10px] text-content-tertiary truncate">{statusLabel}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="space-y-1.5 mb-3">
        {details.map((d, i) => (
          <div key={i} className="flex items-center justify-between text-[11px]">
            <span className="text-[var(--text-secondary)]">{d.label}</span>
            <span className={`font-mono text-content-secondary ${d.truncate ? 'truncate max-w-[140px]' : ''}`}>{d.value}</span>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {badges?.map((b, i) => (
          <span key={i} className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${b.cls}`}>{b.text}</span>
        ))}
        <button onClick={onTest} disabled={testing}
          className="ml-auto px-2 py-1 text-[10px] font-medium rounded border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-inset transition-colors disabled:opacity-50">
          {testing ? t('dataHub.testing') : t('dataHub.test')}
        </button>
      </div>
    </div>
  );
}

/* ─── Collapsible Section ─── */
function Section({ title, icon, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-surface-card border border-line rounded-card shadow-card mb-6 overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 p-4 hover:bg-surface-hover transition-colors text-left">
        <div className="w-8 h-8 rounded-lg bg-surface-inset flex items-center justify-center flex-shrink-0">{icon}</div>
        <span className="text-sm font-semibold text-content flex-1">{title}</span>
        <svg className={`w-4 h-4 text-[var(--text-secondary)] transition-transform ${open ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M19 9l-7 7-7-7" /></svg>
      </button>
      {open && <div className="px-4 pb-4 border-t border-line pt-4">{children}</div>}
    </div>
  );
}

/* ─── Import Modal ─── */
function ImportModal({ job, onClose, t }) {
  const [von, setVon] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [bis, setBis] = useState(new Date().toISOString().slice(0, 10));
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const doImport = () => {
    setRunning(true); setResult(null);
    api(`/api/data-hub/import/${job.source}/${job.job}`, { method: 'POST', body: { von, bis } })
      .then(r => setResult(r))
      .catch(e => setResult({ error: true, message: e.message }))
      .finally(() => setRunning(false));
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-surface-elevated border border-line-hover rounded-card shadow-2xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-content">{t('dataHub.manualImport')}: {job.label}</h3>
            <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-content text-lg">&times;</button>
          </div>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('common.from')}</label>
              <input type="date" value={von} onChange={e => setVon(e.target.value)} className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none" />
            </div>
            <div>
              <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('common.to')}</label>
              <input type="date" value={bis} onChange={e => setBis(e.target.value)} className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none" />
            </div>
          </div>
          <button onClick={doImport} disabled={running}
            className="w-full px-4 py-2 bg-primary-600/20 border border-primary-500/30 rounded-lg text-sm font-medium text-primary-300 hover:bg-primary-600/30 transition-colors disabled:opacity-40 mb-3">
            {running ? t('dataHub.importing') : t('dataHub.startImport')}
          </button>
          {running && (
            <div className="bg-surface-inset rounded-lg p-3 text-center">
              <div className="animate-spin h-5 w-5 border-2 border-primary-400 border-t-transparent rounded-full mx-auto mb-2" />
              <div className="text-xs text-[var(--text-secondary)]">{t('dataHub.importRunning')}</div>
            </div>
          )}
          {result && !result.error && (
            <div className="bg-green-500/5 border border-green-500/10 rounded-lg p-3">
              <div className="flex items-center gap-2 mb-1">
                <span className="w-2 h-2 rounded-full bg-green-500" />
                <span className="text-xs font-semibold text-green-400">{t('dataHub.importComplete')}</span>
                {result.duration_seconds && <span className="text-[10px] text-content-tertiary font-mono ml-auto">{result.duration_seconds}s</span>}
              </div>
              <div className="text-[11px] text-content-secondary">
                {t('dataHub.importStats', { fetched: fmt(result.entries_fetched ?? result.fetched), created: fmt(result.entries_created ?? result.created), updated: fmt(result.entries_updated ?? result.updated) })}
              </div>
            </div>
          )}
          {result?.error && (
            <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 text-xs text-red-400">{result.message}</div>
          )}
        </div>
      </div>
    </>
  );
}

/* ─── Config Modal (sync-job config) ─── */
function ConfigModal({ job, onClose, t }) {
  const [loading, setLoading] = useState(true);
  const [interval, setInterval_] = useState(null);
  const [aktiv, setAktiv] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    api(`/api/data-hub/sync-job-config/${job.source}/${job.job}`)
      .then(cfg => { setInterval_(cfg.interval || null); setAktiv(cfg.aktiv !== false); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [job]);

  const save = () => {
    setSaving(true); setMsg(null);
    api(`/api/data-hub/sync-job-config/${job.source}/${job.job}`, {
      method: 'PUT', body: { interval, aktiv }
    })
      .then(() => setMsg({ ok: true, text: t('common.saved') }))
      .catch(e => setMsg({ ok: false, text: e.message }))
      .finally(() => setSaving(false));
  };

  const intervals = [
    { value: null, label: t('dataHub.off') },
    { value: '1min', label: t('dataHub.1min') },
    { value: '5min', label: t('dataHub.5min') },
    { value: '10min', label: t('dataHub.10min') },
    { value: '15min', label: t('dataHub.15min') },
    { value: '30min', label: t('dataHub.30min') },
    { value: '1h', label: t('dataHub.1hour') },
    { value: 'daily', label: t('dataHub.daily') },
  ];

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-surface-elevated border border-line-hover rounded-card shadow-2xl w-full max-w-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-bold text-content">{t('dataHub.configuration')}: {job.label}</h3>
            <button onClick={onClose} className="text-[var(--text-secondary)] hover:text-content text-lg">&times;</button>
          </div>
          {loading ? (
            <div className="text-xs text-content-tertiary py-4 text-center">{t('common.loading')}</div>
          ) : (
            <>
              <div className="mb-4">
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.syncInterval')}</label>
                <select value={interval || ''} onChange={e => setInterval_(e.target.value || null)}
                  className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content focus:outline-none">
                  {intervals.map(i => <option key={i.value || 'off'} value={i.value || ''}>{i.label}</option>)}
                </select>
              </div>
              <div className="mb-4 flex items-center gap-3">
                <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold">{t('dataHub.active')}</label>
                <ToggleSwitch size="sm" checked={aktiv} onChange={setAktiv} />
              </div>
              <div className="mb-3 text-[11px] text-content-tertiary">
                <div>{t('dataHub.source')}: <span className="font-mono text-content-secondary">{job.source}</span></div>
                <div>{t('dataHub.job')}: <span className="font-mono text-content-secondary">{job.job}</span></div>
                {job.count != null && <div>{t('dataHub.entries')}: <span className="font-mono text-content-secondary">{fmt(job.count)}</span></div>}
              </div>
              <button onClick={save} disabled={saving}
                className="w-full px-4 py-2 bg-primary-600/20 border border-primary-500/30 rounded-lg text-sm font-medium text-primary-300 hover:bg-primary-600/30 transition-colors disabled:opacity-40">
                {saving ? t('common.saving') : t('common.save')}
              </button>
              {msg && <div className={`text-xs mt-2 ${msg.ok ? 'text-green-400' : 'text-red-400'}`}>{msg.text}</div>}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ═══ MAIN COMPONENT ═══ */
export default function ApiConfig() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useTabParam('hub');
  const [sys, setSys] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});
  const [connections, setConnections] = useState({});
  const [syncJobs, setSyncJobs] = useState(null);
  const [syncLog, setSyncLog] = useState(null);
  const [importModal, setImportModal] = useState(null);
  const [configModal, setConfigModal] = useState(null);

  // Channel editing
  const [editingChannel, setEditingChannel] = useState(null);
  const [editChannelName, setEditChannelName] = useState('');

  // Sync-Log filters
  const [logFilter, setLogFilter] = useState({ source: '', status: '' });

  // Odoo form
  const [odooUrl, setOdooUrl] = useState('');
  const [odooDb, setOdooDb] = useState('');
  const [odooUser, setOdooUser] = useState('');
  const [odooPass, setOdooPass] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [showPass, setShowPass] = useState({});
  const [saveMsg, setSaveMsg] = useState({});
  const [saving, setSaving] = useState({});

  // OMS form
  const [omsUrl, setOmsUrl] = useState('');
  const [omsDb, setOmsDb] = useState('');
  const [omsUser, setOmsUser] = useState('');
  const [omsPass, setOmsPass] = useState('');
  const [hasOmsPass, setHasOmsPass] = useState(false);

  // Legacy form
  const [legUrl, setLegUrl] = useState('');
  const [legDb, setLegDb] = useState('');
  const [legUid, setLegUid] = useState('');
  const [legPass, setLegPass] = useState('');
  const [hasLegPass, setHasLegPass] = useState(false);

  // SMTP
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpFromName, setSmtpFromName] = useState('');
  const [hasSmtpPass, setHasSmtpPass] = useState(false);

  // Anthropic
  const [anthropicKey, setAnthropicKey] = useState('');
  const [anthropicStatus, setAnthropicStatus] = useState(null);

  // Initial Sync
  const [initialSync, setInitialSync] = useState(null);
  const [syncPolling, setSyncPolling] = useState(false);

  // Sync Gaps / Nachhol-Sync
  const [syncGaps, setSyncGaps] = useState(null);
  const [gapsLoading, setGapsLoading] = useState(false);
  const [nachholRunning, setNachholRunning] = useState(null);
  const [nachholResult, setNachholResult] = useState(null);

  // Cronjobs
  const [cronjobs, setCronjobs] = useState([]);
  const [cronjobRunning, setCronjobRunning] = useState({});
  const [cronjobPolling, setCronjobPolling] = useState(null);

  // OMS Verbindungen
  const [omsStatus, setOmsStatus] = useState(null);
  const [omsActivity, setOmsActivity] = useState([]);
  const [omsActivityTotal, setOmsActivityTotal] = useState(0);
  const [omsActivityFilter, setOmsActivityFilter] = useState({ user_type: '', erfolg: '', von: '', bis: '' });
  const [omsActivityOffset, setOmsActivityOffset] = useState(0);

  // FlowOMS Inbound (#1214, Phase 0) — Pauls A-System pusht Records an uns
  const [flowoms, setFlowoms] = useState(null);
  const [flowomsLoading, setFlowomsLoading] = useState(false);
  const [flowomsNewToken, setFlowomsNewToken] = useState(null); // Plaintext, nur direkt nach Generate
  const [flowomsAction, setFlowomsAction] = useState(null);     // 'generating' | 'revoking' | null

  // Channels (display names from DB)
  const [channels, setChannels] = useState([]);
  const channelName = useCallback((key, fallback) => {
    const ch = channels.find(c => c.channel_key === key);
    return ch ? ch.display_name : fallback || key;
  }, [channels]);

  const loadSyncLog = useCallback((filters) => {
    const f = filters || logFilter;
    const params = new URLSearchParams({ limit: '30' });
    if (f.source) params.set('source', f.source);
    if (f.status) params.set('status', f.status);
    api(`/api/data-hub/sync-log?${params}`).then(setSyncLog).catch(() => {});
  }, [logFilter]);

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      api('/api/system/status'),
      api('/api/odoo/config').catch(() => null),
      api('/api/data-hub/connections').catch(() => ({})),
      api('/api/data-hub/sync-jobs').catch(() => null),
      api('/api/data-hub/sync-log?limit=30').catch(() => null),
      api('/api/system/smtp').catch(() => null),
      api('/api/system/anthropic').catch(() => null),
      api('/api/data-hub/connection/oms_odoo').catch(() => null),
      api('/api/data-hub/connection/legacy_dev').catch(() => null),
      api('/api/data-hub/channels').catch(() => ({ channels: [] })),
      api('/api/admin/cronjobs').catch(() => []),
    ]).then(([status, cfg, conns, jobs, log, smtp, anthro, omsCfg, legCfg, chData, cronData]) => {
      setSys(status);
      if (chData?.channels) setChannels(chData.channels);
      setConnections(conns || {});
      if (jobs) setSyncJobs(jobs);
      if (log) setSyncLog(log);
      if (cfg) {
        if (cfg.url) setOdooUrl(cfg.url);
        if (cfg.db) setOdooDb(cfg.db);
        if (cfg.user) setOdooUser(cfg.user);
        setHasPassword(!!cfg.has_password);
      }
      if (omsCfg) {
        if (omsCfg.url) setOmsUrl(omsCfg.url);
        if (omsCfg.db) setOmsDb(omsCfg.db);
        if (omsCfg.user) setOmsUser(omsCfg.user);
        setHasOmsPass(!!omsCfg.has_password);
      }
      if (legCfg) {
        if (legCfg.url) setLegUrl(legCfg.url);
        if (legCfg.db) setLegDb(legCfg.db);
        if (legCfg.uid) setLegUid(String(legCfg.uid));
        setHasLegPass(!!legCfg.has_password);
      }
      if (smtp) {
        if (smtp.host) setSmtpHost(smtp.host);
        if (smtp.port) setSmtpPort(String(smtp.port));
        if (smtp.user) setSmtpUser(smtp.user);
        if (smtp.from_address) setSmtpFrom(smtp.from_address);
        if (smtp.from_name) setSmtpFromName(smtp.from_name);
        setHasSmtpPass(!!smtp.has_password);
      }
      if (anthro) setAnthropicStatus(anthro);
      if (Array.isArray(cronData)) setCronjobs(cronData);
    }).catch(() => setSys(null)).finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Load OMS status + activity
  const loadOmsStatus = useCallback(() => {
    api('/api/data-hub/oms/status').then(setOmsStatus).catch(() => {});
  }, []);
  const loadOmsActivity = useCallback((filters, offset = 0) => {
    const f = filters || omsActivityFilter;
    const params = new URLSearchParams({ limit: '50', offset: String(offset) });
    if (f.user_type) params.set('user_type', f.user_type);
    if (f.erfolg !== '') params.set('erfolg', f.erfolg);
    if (f.von) params.set('von', f.von);
    if (f.bis) params.set('bis', f.bis);
    api(`/api/data-hub/oms/activity?${params}`).then(data => {
      if (offset > 0) setOmsActivity(prev => [...prev, ...data.entries]);
      else setOmsActivity(data.entries || []);
      setOmsActivityTotal(data.total || 0);
      setOmsActivityOffset(offset);
    }).catch(() => {});
  }, [omsActivityFilter]);
  useEffect(() => { loadOmsStatus(); loadOmsActivity(); }, [loadOmsStatus, loadOmsActivity]);

  // FlowOMS Inbound loaders (#1214) — passiv, kein Polling zu Paul
  const loadFlowoms = useCallback(() => {
    setFlowomsLoading(true);
    api('/api/data-hub/flowoms/status')
      .then(setFlowoms)
      .catch(() => setFlowoms(null))
      .finally(() => setFlowomsLoading(false));
  }, []);
  useEffect(() => { if (activeTab === 'flowoms') loadFlowoms(); }, [activeTab, loadFlowoms]);

  function flowomsGenerateToken() {
    const replace = flowoms?.token?.configured;
    const ok = replace
      ? window.confirm(t('dataHub.flowoms.confirmReplace'))
      : window.confirm(t('dataHub.flowoms.confirmGenerate'));
    if (!ok) return;
    setFlowomsAction('generating');
    setFlowomsNewToken(null);
    api('/api/data-hub/flowoms/token/generate', { method: 'POST' })
      .then(r => { setFlowomsNewToken(r.token); loadFlowoms(); })
      .catch(e => alert(`Token-Gen fehlgeschlagen: ${e.message}`))
      .finally(() => setFlowomsAction(null));
  }

  function flowomsRevokeToken() {
    if (!window.confirm(t('dataHub.flowoms.confirmRevoke'))) return;
    setFlowomsAction('revoking');
    api('/api/data-hub/flowoms/token', { method: 'DELETE' })
      .then(() => { setFlowomsNewToken(null); loadFlowoms(); })
      .catch(e => alert(`Revoke fehlgeschlagen: ${e.message}`))
      .finally(() => setFlowomsAction(null));
  }

  // Refresh sync-jobs every 30s
  useEffect(() => {
    const iv = setInterval(() => {
      api('/api/data-hub/sync-jobs').then(setSyncJobs).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  /* ─── Test functions ─── */
  function testConnection(source) {
    setTesting(prev => ({ ...prev, [source]: true }));
    api(`/api/data-hub/test-connection/${source}`, { method: 'POST' })
      .then(() => { api('/api/data-hub/connections').then(setConnections).catch(() => {}); })
      .catch(() => { api('/api/data-hub/connections').then(setConnections).catch(() => {}); })
      .finally(() => setTesting(prev => ({ ...prev, [source]: false })));
  }

  function testBackend() {
    setTesting(prev => ({ ...prev, backend: true }));
    const start = Date.now();
    fetch('/api/health').then(r => r.json()).then(() => {
      setSys(s => s ? { ...s, backend: { ...s.backend, response_ms: Date.now() - start, status: 'online' } } : s);
    }).catch(() => {}).finally(() => setTesting(prev => ({ ...prev, backend: false })));
  }

  function testPostgres() {
    setTesting(prev => ({ ...prev, postgres: true }));
    api('/api/system/status').then(setSys).catch(() => {}).finally(() => setTesting(prev => ({ ...prev, postgres: false })));
  }

  function triggerSync(source, job) {
    setTesting(prev => ({ ...prev, [`sync_${source}_${job}`]: true }));
    api(`/api/data-hub/sync/${source}/${job}`, { method: 'POST' })
      .then(() => {
        api('/api/data-hub/sync-jobs').then(setSyncJobs).catch(() => {});
        loadSyncLog();
      })
      .catch(() => {})
      .finally(() => setTesting(prev => ({ ...prev, [`sync_${source}_${job}`]: false })));
  }

  function saveConnection(source, body) {
    setSaving(s => ({ ...s, [source]: true }));
    setSaveMsg(s => ({ ...s, [source]: null }));
    const endpoint = source === 'weship_odoo' ? '/api/odoo/config' : `/api/data-hub/connection/${source}`;
    api(endpoint, { method: 'POST', body })
      .then(r => setSaveMsg(s => ({ ...s, [source]: { ok: true, text: r.message } })))
      .catch(e => setSaveMsg(s => ({ ...s, [source]: { ok: false, text: e.message } })))
      .finally(() => setSaving(s => ({ ...s, [source]: false })));
  }

  function saveChannelName(channelKey, displayName) {
    api(`/api/data-hub/channels/${channelKey}`, { method: 'PUT', body: { display_name: displayName } })
      .then(() => { api('/api/data-hub/sync-jobs').then(setSyncJobs).catch(() => {}); })
      .catch(() => {})
      .finally(() => setEditingChannel(null));
  }

  function startInitialSync() {
    api('/api/data-hub/initial-sync', { method: 'POST' })
      .then(() => {
        setSyncPolling(true);
        pollSyncStatus();
      })
      .catch(e => setInitialSync({ error: e.message }));
  }

  function pollSyncStatus() {
    const poll = () => {
      api('/api/data-hub/initial-sync/status')
        .then(data => {
          setInitialSync(data);
          if (data.running) {
            setTimeout(poll, 1500);
          } else {
            setSyncPolling(false);
            // Refresh data after sync complete
            loadAll();
          }
        })
        .catch(() => setSyncPolling(false));
    };
    poll();
  }

  function loadSyncGaps() {
    setGapsLoading(true);
    api('/api/data-hub/sync-gaps')
      .then(data => setSyncGaps(data))
      .catch(() => setSyncGaps(null))
      .finally(() => setGapsLoading(false));
  }

  function triggerNachhol(von, bis, typ) {
    setNachholRunning(typ);
    setNachholResult(null);
    api('/api/data-hub/sync-nachholen', { method: 'POST', body: { von, bis, typ } })
      .then(data => {
        setNachholResult(data);
        loadSyncGaps();
        loadSyncLog();
        api('/api/data-hub/sync-jobs').then(setSyncJobs).catch(() => {});
      })
      .catch(e => setNachholResult({ error: e.message }))
      .finally(() => setNachholRunning(null));
  }

  if (loading) return (
    <div className="p-page">
      <PageHeader title="Data Hub" subtitle={t('dataHub.subtitle')} />
      <div className="text-sm text-content-tertiary mt-8">{t('dataHub.loadingStatus')}</div>
    </div>
  );

  const b = sys?.backend || {};
  const pg = sys?.postgres || {};
  const si = sys?.system || {};
  const wOdoo = connections.weship_odoo || {};
  const omsOdoo = connections.oms_odoo || {};
  const legDev = connections.legacy_dev || {};

  const connStatus = (c) => c.last_test_ok === true ? 'verbunden' : c.last_test_ok === false ? 'fehler' : c.url ? 'konfiguriert' : 'nicht_konfiguriert';
  const connLabel = (c) => c.last_test_ok === true ? t('dataHub.connected') : c.last_test_ok === false ? t('dataHub.error') : c.url ? t('dataHub.configured') : t('dataHub.notConfigured');

  // Group sync jobs by source — use backend jobGroups if available
  const jobGroups = syncJobs?.jobGroups || [
    { key: 'weship_odoo', label: channelName('weship_odoo', 'WeShip Odoo'), color: '#f59e0b' },
    { key: 'oms_odoo', label: channelName('oms_odoo', 'OMS Helpdesk'), color: '#e74c3c' },
    { key: 'legacy_dev', label: channelName('legacy_dev', 'Performance API'), color: '#4D9CE0' },
  ];
  const jobs = syncJobs?.jobs || [];

  // Filtered sync-log entries
  const filteredLog = (syncLog?.entries || []).filter(e => {
    if (logFilter.source && e.source !== logFilter.source) return false;
    if (logFilter.status) {
      if (logFilter.status === 'error' && e.status !== 'error') return false;
      if (logFilter.status === 'success' && e.status !== 'success') return false;
      if (logFilter.status === 'running' && e.status !== 'running') return false;
    }
    return true;
  });

  return (
    <div className="p-page">
      <PageHeader title="Data Hub" subtitle={t('dataHub.subtitle')} className="mb-6" />

      {/* ─── TAB BAR ─── */}
      <div className="flex gap-1 bg-surface-card border border-line p-1 rounded-lg w-fit mb-6">
        {[['hub', 'Data Hub'], ['oms', t('dataHub.omsVerbindungen')], ['flowoms', 'FlowOMS Inbound']].map(([key, lbl]) => (
          <button key={key} onClick={() => setActiveTab(key)} className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === key ? 'bg-white dark:bg-surface-elevated shadow-sm text-content font-semibold' : 'text-[var(--text-secondary)] hover:text-content'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {activeTab === 'hub' && (<>
      {/* ─── 1. STATUS CARDS (5) ─── */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3 mb-6">
        <ConnCard title="Backend" color="#34d399" t={t}
          icon={<svg width="16" height="16" fill="none" stroke="#34d399" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M22 12h-4l-3 9L9 3l-3 9H2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          status={b.status} statusLabel={b.status === 'online' ? t('dataHub.online') : t('dataHub.offline')}
          details={[{ label: 'Response', value: `${b.response_ms || '–'} ms` }, { label: 'PID', value: String(si.pid || '–') }]}
          onTest={testBackend} testing={testing.backend} />

        <ConnCard title="PostgreSQL" color="#338edc" t={t}
          icon={<svg width="16" height="16" fill="none" stroke="#338edc" strokeWidth="1.5" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>}
          status={pg.status} statusLabel={pg.status === 'connected' ? t('dataHub.connected') : t('dataHub.disconnected')}
          details={[{ label: t('dataHub.tables'), value: String(pg.tables || 0) }, { label: t('dataHub.size'), value: pg.db_size || '–' }]}
          onTest={testPostgres} testing={testing.postgres} />

        <ConnCard title={channelName('weship_odoo', 'WeShip Odoo')} color="#f59e0b" t={t}
          icon={<svg width="16" height="16" fill="none" stroke="#f59e0b" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M12 2a3 3 0 00-3 3v1a3 3 0 006 0V5a3 3 0 00-3-3z" strokeLinecap="round" /></svg>}
          status={connStatus(wOdoo)} statusLabel={connLabel(wOdoo)}
          details={[{ label: 'URL', value: wOdoo.url || '–', truncate: true }, { label: 'Test', value: wOdoo.last_test ? timeAgo(wOdoo.last_test, t) : t('dataHub.never') }]}
          onTest={() => testConnection('weship_odoo')} testing={testing.weship_odoo}
          badges={[{ text: 'READ-ONLY', cls: 'bg-red-500/10 text-red-400 border border-red-500/20' }]} />

        <ConnCard title={channelName('oms_odoo', 'OMS Helpdesk')} color="#e74c3c" t={t}
          icon={<svg width="16" height="16" fill="none" stroke="#e74c3c" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" strokeLinecap="round" /></svg>}
          status={connStatus(omsOdoo)} statusLabel={connLabel(omsOdoo)}
          details={[{ label: 'URL', value: omsOdoo.url || '–', truncate: true }, { label: 'Test', value: omsOdoo.last_test ? timeAgo(omsOdoo.last_test, t) : t('dataHub.never') }]}
          onTest={() => testConnection('oms_odoo')} testing={testing.oms_odoo} />

        <ConnCard title={channelName('legacy_dev', 'Performance API')} color="#4D9CE0" t={t}
          icon={<svg width="16" height="16" fill="none" stroke="#4D9CE0" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          status={connStatus(legDev)} statusLabel={connLabel(legDev)}
          details={[{ label: 'URL', value: legDev.url || '–', truncate: true }, { label: 'UID', value: String(legDev.uid || '–') }]}
          onTest={() => testConnection('legacy_dev')} testing={testing.legacy_dev}
          badges={[
            { text: 'READ-ONLY', cls: 'bg-amber-500/10 text-amber-400 border border-amber-500/20' },
            { text: 'Dev Server', cls: 'bg-amber-500/10 text-amber-400 border border-amber-500/20' },
          ]} />
      </div>

      {/* ─── 2. SYNC-JOBS TABLE ─── */}
      <div className="bg-surface-card border border-line rounded-card shadow-card mb-6 overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-line">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
            <svg width="16" height="16" fill="none" stroke="#06b6d4" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M4 4v5h5" strokeLinecap="round" strokeLinejoin="round" /><path d="M20 20v-5h-5" strokeLinecap="round" strokeLinejoin="round" /><path d="M4 9a9 9 0 0114.7-4.7L20 6M20 15a9 9 0 01-14.7 4.7L4 18" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </div>
          <div className="text-sm font-semibold text-content">{t('dataHub.syncJobs')}</div>
        </div>

        {jobGroups.map(group => {
          const groupJobs = jobs.filter(j => j.source === group.key);
          if (groupJobs.length === 0) return null;
          return (
            <div key={group.key}>
              <div className="px-4 py-2 bg-surface-inset/50 border-b border-line flex items-center gap-2">
                {editingChannel === group.key ? (
                  <div className="flex items-center gap-2">
                    <input type="text" value={editChannelName} onChange={e => setEditChannelName(e.target.value)}
                      className="px-2 py-0.5 bg-surface-inset border border-line-hover rounded text-[10px] font-bold uppercase tracking-wider text-content focus:outline-none"
                      style={{ color: group.color, width: '200px' }}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveChannelName(group.key, editChannelName); if (e.key === 'Escape') setEditingChannel(null); }}
                    />
                    <button onClick={() => saveChannelName(group.key, editChannelName)}
                      className="text-[10px] text-emerald-400 hover:text-emerald-300">{t('common.save')}</button>
                    <button onClick={() => setEditingChannel(null)}
                      className="text-[10px] text-[var(--text-secondary)] hover:text-content">{t('common.cancel')}</button>
                  </div>
                ) : (
                  <span className="text-[10px] font-bold uppercase tracking-wider cursor-pointer hover:underline"
                    style={{ color: group.color }}
                    onClick={() => { setEditingChannel(group.key); setEditChannelName(group.label); }}
                    title={t('dataHub.clickToRename')}>
                    {group.icon ? `${group.icon} ` : ''}{group.label}
                  </span>
                )}
              </div>
              <div className="divide-y divide-line">
                {groupJobs.map(job => {
                  const last = job.last;
                  const isRunning = testing[`sync_${job.source}_${job.job}`];
                  const StatusIcon = !last ? null : last.status === 'success' ? CheckCircle : last.status === 'error' ? XCircle : Clock;
                  const statusColor = !last ? '' : last.status === 'success' ? 'text-green-400' : last.status === 'error' ? 'text-red-400' : 'text-amber-400';
                  return (
                    <div key={`${job.source}:${job.job}`} className="flex items-center gap-3 px-4 py-2.5 hover:bg-surface-hover transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-content">{job.label}</div>
                        <div className="text-[10px] text-content-tertiary mt-0.5">
                          {(() => { const m = { '1min': 'dataHub.1min', '5min': 'dataHub.5min', '10min': 'dataHub.10min', '15min': 'dataHub.15min', '30min': 'dataHub.30min', '1h': 'dataHub.1hour', 'daily': 'dataHub.daily', 'daily 03:00': 'dataHub.daily' }; return `⏱ ${t(m[job.interval] || 'dataHub.onDemand')}`; })()}
                        </div>
                      </div>
                      <div className="text-[11px] text-content-tertiary w-24 text-center">
                        {last ? <span className="flex items-center gap-1">{timeAgo(last.started_at, t)} {StatusIcon && <StatusIcon className={`w-3.5 h-3.5 ${statusColor}`} />}</span> : '–'}
                      </div>
                      <div className="text-[11px] font-mono text-content-secondary w-24 text-right">
                        {job.count != null ? <>{fmt(job.count)} {job.countLabel}</> : '–'}
                      </div>
                      <div className="flex items-center gap-1.5 w-36 justify-end flex-shrink-0">
                        <button onClick={() => triggerSync(job.source, job.job)} disabled={isRunning}
                          className="px-2 py-1 text-[10px] font-medium rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20 transition-colors disabled:opacity-40"
                          title={t('dataHub.syncNow')}>
                          {isRunning ? '...' : '▶'}
                        </button>
                        {['mitarbeiter', 'kunden', 'waves', 'packs'].includes(job.job) && (
                          <button onClick={() => setImportModal(job)}
                            className="px-2 py-1 text-[10px] font-medium rounded bg-primary-500/10 border border-primary-500/20 text-primary-400 hover:bg-primary-500/20 transition-colors"
                            title={t('dataHub.manualImport')}><Download className="w-3 h-3" /></button>
                        )}
                        <button onClick={() => setConfigModal(job)}
                          className="px-2 py-1 text-[10px] font-medium rounded bg-gray-500/10 border border-gray-500/20 text-[var(--text-secondary)] hover:text-content hover:bg-gray-500/20 transition-colors"
                          title={t('dataHub.configuration')}><Settings className="w-3 h-3" /></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── INITIAL SYNC ─── */}
      <div className="bg-surface-card border border-line rounded-card shadow-card mb-6 overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-line">
          <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center"><RefreshCw className="w-4 h-4 text-emerald-400" /></div>
          <div className="text-sm font-semibold text-content">{t('dataHub.initialSync')}</div>
          <div className="ml-auto">
            <button
              onClick={startInitialSync}
              disabled={syncPolling || initialSync?.running}
              className="px-3 py-1.5 text-xs font-medium rounded-button bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/30 transition-colors disabled:opacity-40">
              {syncPolling ? t('dataHub.running') : t('dataHub.syncAllData')}
            </button>
          </div>
        </div>
        {initialSync && (
          <div className="p-4">
            {/* Progress bar */}
            <div className="mb-3">
              <div className="flex items-center justify-between text-[10px] text-[var(--text-secondary)] mb-1">
                <span>{t('dataHub.stepOf', { step: initialSync.step || 0, total: initialSync.totalSteps || 7 })}</span>
                <span>{initialSync.running ? t('dataHub.running') : initialSync.error ? t('dataHub.error') : t('dataHub.finished')}</span>
              </div>
              <div className="w-full h-2 bg-surface-inset rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${initialSync.error ? 'bg-red-500' : initialSync.running ? 'bg-emerald-500' : 'bg-green-500'}`}
                  style={{ width: `${((initialSync.step || 0) / (initialSync.totalSteps || 7)) * 100}%` }}
                />
              </div>
            </div>
            {/* Step list */}
            <div className="space-y-1">
              {(initialSync.steps || []).map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`w-4 h-4 flex items-center justify-center rounded-full text-[9px] font-bold ${
                    step.status === 'success' ? 'bg-green-500/20 text-green-400'
                    : step.status === 'error' ? 'bg-red-500/20 text-red-400'
                    : 'bg-amber-500/20 text-amber-400'
                  }`}>
                    {step.status === 'success' ? '✓' : step.status === 'error' ? '✕' : '⟳'}
                  </span>
                  <span className={`flex-1 ${step.status === 'running' ? 'text-content' : 'text-[var(--text-secondary)]'}`}>{step.label}</span>
                  {step.status === 'error' && <span className="text-[10px] text-red-400 truncate max-w-[200px]" title={step.error}>{step.error}</span>}
                  {step.status === 'success' && step.result && (
                    <span className="text-[10px] text-content-tertiary font-mono">{step.result.duration_seconds || 0}s</span>
                  )}
                </div>
              ))}
            </div>
            {initialSync.finishedAt && !initialSync.running && (
              <div className="mt-3 pt-2 border-t border-line text-[10px] text-content-tertiary">
                {t('dataHub.completedAt')}: {new Date(initialSync.finishedAt).toLocaleString('de-DE')}
              </div>
            )}
          </div>
        )}
        {!initialSync && (
          <div className="p-4 text-xs text-content-tertiary">
            {t('dataHub.initialSyncDesc')}
          </div>
        )}
      </div>

      {/* ─── SYNC-LÜCKEN (Gap Detection + Nachhol-Sync) ─── */}
      <div className="bg-surface-card border border-line rounded-card shadow-card mb-6 overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-line">
          <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-amber-400" /></div>
          <div className="text-sm font-semibold text-content">{t('dataHub.syncGaps')}</div>
          <div className="ml-auto">
            <button onClick={loadSyncGaps} disabled={gapsLoading}
              className="px-3 py-1.5 text-xs font-medium rounded-button bg-amber-600/20 border border-amber-500/30 text-amber-300 hover:bg-amber-600/30 transition-colors disabled:opacity-40">
              {gapsLoading ? t('dataHub.checking') : t('dataHub.checkGaps')}
            </button>
          </div>
        </div>

        {!syncGaps && !gapsLoading && (
          <div className="p-4 text-xs text-content-tertiary">
            {t('dataHub.checkGapsDesc')}
          </div>
        )}

        {gapsLoading && (
          <div className="p-4 flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            <div className="animate-spin h-4 w-4 border-2 border-amber-500 border-t-transparent rounded-full" />
            {t('dataHub.checkingDatabase')}
          </div>
        )}

        {syncGaps && !gapsLoading && (
          <div className="p-4 space-y-4">
            {/* Waves Gaps */}
            <div className="bg-surface-inset rounded-card p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-content flex items-center gap-1"><Package className="w-3.5 h-3.5" /> Waves</span>
                <span className="text-[10px] text-content-tertiary">
                  {t('dataHub.lastEntry')}: {syncGaps.waves?.lastDate || '–'}
                </span>
                {syncGaps.waves?.gapDays > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500/10 text-red-400">
                    {t('dataHub.daysMissing', { days: syncGaps.waves.gapDays })}
                  </span>
                )}
                {syncGaps.waves?.gapDays === 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-500/10 text-green-400">
                    {t('dataHub.noGaps')}
                  </span>
                )}
              </div>
              {syncGaps.waves?.gapDays > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[11px] text-[var(--text-secondary)]">
                    {t('dataHub.missing')}: {syncGaps.waves.gaps?.[0]?.von || syncGaps.nachholenSuggestion?.waves?.von} → {syncGaps.waves.gaps?.[syncGaps.waves.gaps.length - 1]?.bis || syncGaps.nachholenSuggestion?.waves?.bis}
                  </span>
                  <button
                    onClick={() => {
                      const s = syncGaps.nachholenSuggestion?.waves;
                      if (s) triggerNachhol(s.von, s.bis, 'waves');
                    }}
                    disabled={nachholRunning === 'waves'}
                    className="ml-auto px-3 py-1 text-[10px] font-medium rounded bg-primary-500/10 border border-primary-500/20 text-primary-400 hover:bg-primary-500/20 transition-colors disabled:opacity-40">
                    {nachholRunning === 'waves' ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('dataHub.syncing')}</span> : <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {t('dataHub.catchUpWaves')}</span>}
                  </button>
                </div>
              )}
            </div>

            {/* Packs Gaps */}
            <div className="bg-surface-inset rounded-card p-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-semibold text-content flex items-center gap-1"><ClipboardList className="w-3.5 h-3.5" /> Packs</span>
                <span className="text-[10px] text-content-tertiary">
                  {t('dataHub.lastEntry')}: {syncGaps.packs?.lastDate || '–'}
                </span>
                {syncGaps.packs?.gapDays > 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-red-500/10 text-red-400">
                    {t('dataHub.daysMissing', { days: syncGaps.packs.gapDays })}
                  </span>
                )}
                {syncGaps.packs?.gapDays === 0 && (
                  <span className="ml-auto px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-green-500/10 text-green-400">
                    {t('dataHub.noGaps')}
                  </span>
                )}
              </div>
              {syncGaps.packs?.gapDays > 0 && (
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-[11px] text-[var(--text-secondary)]">
                    {t('dataHub.missing')}: {syncGaps.packs.gaps?.[0]?.von || syncGaps.nachholenSuggestion?.packs?.von} → {syncGaps.packs.gaps?.[syncGaps.packs.gaps.length - 1]?.bis || syncGaps.nachholenSuggestion?.packs?.bis}
                  </span>
                  <button
                    onClick={() => {
                      const s = syncGaps.nachholenSuggestion?.packs;
                      if (s) triggerNachhol(s.von, s.bis, 'packs');
                    }}
                    disabled={nachholRunning === 'packs'}
                    className="ml-auto px-3 py-1 text-[10px] font-medium rounded bg-primary-500/10 border border-primary-500/20 text-primary-400 hover:bg-primary-500/20 transition-colors disabled:opacity-40">
                    {nachholRunning === 'packs' ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('dataHub.syncing')}</span> : <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {t('dataHub.catchUpPacks')}</span>}
                  </button>
                </div>
              )}
            </div>

            {/* Both button */}
            {(syncGaps.waves?.gapDays > 0 || syncGaps.packs?.gapDays > 0) && (
              <div className="flex justify-end">
                <button
                  onClick={() => {
                    const s = syncGaps.nachholenSuggestion;
                    if (s) triggerNachhol(s.waves?.von || s.packs?.von, s.waves?.bis || s.packs?.bis, 'both');
                  }}
                  disabled={nachholRunning === 'both'}
                  className="px-4 py-1.5 text-xs font-medium rounded-lg bg-emerald-600/20 border border-emerald-500/30 text-emerald-300 hover:bg-emerald-600/30 transition-colors disabled:opacity-40">
                  {nachholRunning === 'both' ? <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {t('dataHub.syncingBoth')}</span> : <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {t('dataHub.retryFailed')}</span>}
                </button>
              </div>
            )}

            {/* Nachhol Result */}
            {nachholResult && (
              <div className={`rounded-card p-3 text-xs ${nachholResult.error ? 'bg-red-500/10 border border-red-500/20' : 'bg-green-500/10 border border-green-500/20'}`}>
                {nachholResult.error ? (
                  <span className="text-red-400">{t('dataHub.error')}: {nachholResult.error}</span>
                ) : (
                  <div className="space-y-1">
                    <div className="font-semibold text-green-400">{nachholResult.message}</div>
                    <div className="text-[var(--text-secondary)]">
                      Waves: {nachholResult.totalWaves || 0} | Packs: {nachholResult.totalPacks || 0} | Tage: {nachholResult.totalDays || 0}
                      {nachholResult.failedDays > 0 && <span className="text-red-400 ml-2">({t('dataHub.daysFailedCount', { days: nachholResult.failedDays })})</span>}
                    </div>
                    {nachholResult.days?.filter(d => d.waveError || d.packError).length > 0 && (
                      <div className="mt-2 space-y-0.5">
                        <div className="text-[var(--text-secondary)] font-semibold">{t('dataHub.failedDays')}:</div>
                        {nachholResult.days.filter(d => d.waveError || d.packError).map(d => (
                          <div key={d.date} className="flex items-center gap-2">
                            <span className="font-mono text-red-400">{d.date}</span>
                            <span className="text-content-tertiary truncate">{d.waveError || d.packError}</span>
                            <button
                              onClick={() => triggerNachhol(d.date, d.date, d.waveError ? 'waves' : 'packs')}
                              disabled={!!nachholRunning}
                              className="ml-auto px-2 py-0.5 text-[9px] font-medium rounded bg-primary-500/10 border border-primary-500/20 text-primary-400 hover:bg-primary-500/20 transition-colors disabled:opacity-40">
                              <span className="flex items-center gap-1"><RefreshCw className="w-3 h-3" /> {t('dataHub.retry')}</span>
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ─── 3. SYNC-LOG (with filters) ─── */}
      <div className="bg-surface-card border border-line rounded-card shadow-card mb-6 overflow-hidden">
        <div className="flex items-center gap-3 p-4 border-b border-line">
          <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
            <svg width="16" height="16" fill="none" stroke="#a855f7" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" strokeLinecap="round" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>
          </div>
          <div className="text-sm font-semibold text-content">{t('dataHub.syncLog')}</div>

          {/* Filters */}
          <div className="ml-auto flex items-center gap-2">
            <select value={logFilter.source} onChange={e => { const f = { ...logFilter, source: e.target.value }; setLogFilter(f); loadSyncLog(f); }}
              className="px-2 py-1 text-[10px] bg-surface-inset border border-line-hover rounded text-content-secondary focus:outline-none">
              <option value="">{t('dataHub.allSources')}</option>
              <option value="weship_odoo">{channelName('weship_odoo', 'WeShip Odoo')}</option>
              <option value="oms_odoo">{channelName('oms_odoo', 'OMS Helpdesk')}</option>
              <option value="legacy_dev">{channelName('legacy_dev', 'Performance API')}</option>
            </select>
            <select value={logFilter.status} onChange={e => { const f = { ...logFilter, status: e.target.value }; setLogFilter(f); loadSyncLog(f); }}
              className="px-2 py-1 text-[10px] bg-surface-inset border border-line-hover rounded text-content-secondary focus:outline-none">
              <option value="">{t('common.allStatus')}</option>
              <option value="success">OK</option>
              <option value="error">{t('dataHub.error')}</option>
              <option value="running">Running</option>
            </select>
            <button onClick={() => loadSyncLog()}
              className="px-2 py-1 text-[10px] font-medium rounded border border-line-hover text-content-tertiary hover:text-content hover:bg-surface-inset transition-colors">
              {t('common.refresh')}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line bg-surface-inset/30">
                <th className="px-3 py-2 text-left">{t('dataHub.timestamp')}</th>
                <th className="px-3 py-2 text-left">{t('dataHub.sourceJob')}</th>
                <th className="px-3 py-2 text-center">{t('dataHub.type')}</th>
                <th className="px-3 py-2 text-right">{t('dataHub.entries')}</th>
                <th className="px-3 py-2 text-right">{t('dataHub.created')}</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-right">{t('dataHub.duration')}</th>
              </tr>
            </thead>
            <tbody>
              {filteredLog.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-content-tertiary">{t('dataHub.noSyncEntries')}</td></tr>
              ) : (filteredLog.map(e => (
                <tr key={e.id} className={`border-b border-line hover:bg-surface-hover transition-colors ${e.status === 'error' ? 'bg-red-500/5' : ''}`}
                  title={e.error_message || ''}>
                  <td className="px-3 py-2 font-mono text-content-tertiary">{timeAgo(e.started_at, t)}</td>
                  <td className="px-3 py-2 text-content-secondary">{e.source} / {e.job}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${e.typ === 'manual' || e.typ === 'import' ? 'bg-primary-500/10 text-primary-400' : 'bg-surface-inset text-[var(--text-secondary)]'}`}>
                      {e.typ === 'auto' ? t('dataHub.auto') : e.typ === 'import' ? t('dataHub.import') : t('dataHub.manual')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-content-secondary">{e.entries_fetched || 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-content-secondary">
                    {(e.entries_created || 0) > 0 ? `${e.entries_created} ${t('dataHub.new')}` : '–'}
                    {(e.entries_updated || 0) > 0 ? ` (${e.entries_updated} ${t('dataHub.upd')})` : ''}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                      e.status === 'success' ? 'bg-green-500/10 text-green-400'
                      : e.status === 'error' ? 'bg-red-500/10 text-red-400'
                      : 'bg-amber-500/10 text-amber-400'
                    }`}>
                      {e.status === 'success' ? 'OK' : e.status === 'error' ? t('dataHub.error') : e.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-content-tertiary">{e.duration_seconds ? `${e.duration_seconds}s` : '–'}</td>
                </tr>
              )))}
            </tbody>
          </table>
        </div>
      </div>

      </>)}

      {activeTab === 'oms' && (<>
      {/* ─── OMS VERBINDUNGEN TAB ─── */}

        {/* FlowAI Status KPI Cards */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-content mb-3">{t('dataHub.flowaiStatus')}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-surface-inset border border-line rounded-lg p-3">
              <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">Status</div>
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${omsStatus?.flowai?.connected ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className="text-sm font-bold text-[var(--text-primary)]">
                  {omsStatus?.flowai?.connected ? t('dataHub.verbunden') : t('dataHub.fehler')}
                </span>
              </div>
              <div className="text-[10px] text-content-tertiary mt-0.5">FlowAI Agent</div>
            </div>
            <div className="bg-surface-inset border border-line rounded-lg p-3">
              <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.letzterSync')}</div>
              <div className="text-sm font-bold text-[var(--text-primary)]">{omsStatus?.flowai?.last_sync ? timeAgo(omsStatus.flowai.last_sync, t) : '–'}</div>
            </div>
            <div className="bg-surface-inset border border-line rounded-lg p-3">
              <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.aktionenHeute')}</div>
              <div className="text-sm font-bold text-[var(--text-primary)]">{omsStatus?.flowai?.actions_today ?? 0}</div>
            </div>
            <div className="bg-surface-inset border border-line rounded-lg p-3">
              <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.fehlerHeute')}</div>
              <div className={`text-sm font-bold ${(omsStatus?.flowai?.errors_today || 0) > 0 ? 'text-red-500' : 'text-[var(--text-primary)]'}`}>
                {omsStatus?.flowai?.errors_today ?? 0}
              </div>
            </div>
          </div>
        </div>

        {/* App-User Verbindungen */}
        <div className="mb-4">
          <div className="text-xs font-semibold text-content mb-3">{t('dataHub.appUserVerbindungen')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line">
                  <th className="px-3 py-2 text-left">Avatar</th>
                  <th className="px-3 py-2 text-left">{t('common.name')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.omsAccount')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.letzterEinsatz')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.status')}</th>
                </tr>
              </thead>
              <tbody>
                {(omsStatus?.users || []).map(u => {
                  const initials = `${(u.vorname || '')[0] || ''}${(u.nachname || '')[0] || ''}`.toUpperCase();
                  const isConfigured = !!u.oms_username && u.oms_connection_verified;
                  return (
                    <tr key={u.id} className="border-b border-line hover:bg-surface-hover transition-colors">
                      <td className="px-3 py-2">
                        <div className="w-7 h-7 rounded-full bg-primary-500/10 text-primary-400 flex items-center justify-center text-[10px] font-bold">
                          {initials}
                        </div>
                      </td>
                      <td className="px-3 py-2 font-medium text-content">{u.vorname} {u.nachname}</td>
                      <td className="px-3 py-2 text-content-tertiary font-mono">{u.oms_username || '–'}</td>
                      <td className="px-3 py-2 text-content-tertiary">{u.last_activity ? timeAgo(u.last_activity, t) : '–'}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center gap-1.5 text-[10px] font-medium px-2 py-0.5 rounded-full ${
                          isConfigured ? 'bg-green-500/10 text-green-500' : 'bg-gray-500/10 text-[var(--text-tertiary)]'
                        }`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-gray-400'}`} />
                          {isConfigured ? t('dataHub.aktiv') : t('dataHub.nichtKonfiguriert')}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {(!omsStatus?.users || omsStatus.users.length === 0) && (
                  <tr><td colSpan={5} className="px-3 py-4 text-center text-content-tertiary">{t('dataHub.keineEintraege')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Activity Log */}
        <div>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <div className="text-xs font-semibold text-content">{t('dataHub.activityLog')}</div>
            <div className="flex items-center gap-1.5 ml-auto flex-wrap">
              {['', 'flowai', 'app_user'].map(ut => (
                <button key={ut || 'all'}
                  onClick={() => { const f = { ...omsActivityFilter, user_type: ut }; setOmsActivityFilter(f); loadOmsActivity(f); }}
                  className={`px-2 py-1 text-[10px] rounded font-medium transition-colors ${
                    omsActivityFilter.user_type === ut
                      ? 'bg-primary-500/20 text-primary-400 border border-primary-500/30'
                      : 'bg-surface-inset border border-line text-[var(--text-secondary)] hover:text-content'
                  }`}>
                  {ut === '' ? t('dataHub.alle') : ut === 'flowai' ? t('dataHub.flowai') : t('dataHub.appUser')}
                </button>
              ))}
              <label className="flex items-center gap-1 ml-2 cursor-pointer">
                <input type="checkbox" className="accent-primary-500"
                  checked={omsActivityFilter.erfolg === 'false'}
                  onChange={e => { const f = { ...omsActivityFilter, erfolg: e.target.checked ? 'false' : '' }; setOmsActivityFilter(f); loadOmsActivity(f); }} />
                <span className="text-[10px] text-[var(--text-secondary)]">{t('dataHub.nurFehler')}</span>
              </label>
              <input type="date" value={omsActivityFilter.von}
                onChange={e => { const f = { ...omsActivityFilter, von: e.target.value }; setOmsActivityFilter(f); loadOmsActivity(f); }}
                className="px-2 py-1 text-[10px] bg-surface-inset border border-line rounded text-content ml-2" />
              <input type="date" value={omsActivityFilter.bis}
                onChange={e => { const f = { ...omsActivityFilter, bis: e.target.value }; setOmsActivityFilter(f); loadOmsActivity(f); }}
                className="px-2 py-1 text-[10px] bg-surface-inset border border-line rounded text-content" />
            </div>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-[10px] text-[var(--text-secondary)] uppercase tracking-wider border-b border-line sticky top-0 bg-surface-card">
                  <th className="px-3 py-2 text-left">{t('dataHub.zeit')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.wer')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.aktion')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.case')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.details')}</th>
                  <th className="px-3 py-2 text-left">{t('dataHub.status')}</th>
                </tr>
              </thead>
              <tbody>
                {omsActivity.map(e => {
                  const AKTION_MAP = {
                    'adressklaerung_email_gesendet': t('dataHub.klaerungsanfrageGesendet'),
                    'stage_change': t('dataHub.stageGeaendert'),
                    'comment': t('dataHub.kommentarHinzugefuegt'),
                    'delete': t('dataHub.caseGeloescht'),
                    'adressklaerung_fehler': t('dataHub.adressklaerungFehler'),
                    'adressklaerung_abgeschlossen': t('dataHub.adressklaerungAbgeschlossen'),
                  };
                  const aktionLabel = AKTION_MAP[e.aktion] || e.aktion;
                  const d = e.details || {};
                  let detailStr = '';
                  if (d.to_stage) detailStr = `→ ${d.to_stage}`;
                  else if (d.empfaenger) detailStr = d.empfaenger;
                  else if (d.intern !== undefined) detailStr = d.intern ? 'Internal' : 'Public';

                  return (
                    <tr key={e.id} className={`border-b border-line hover:bg-surface-hover transition-colors ${!e.erfolg ? 'bg-red-500/5' : ''}`}>
                      <td className="px-3 py-2 text-content-tertiary whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString('de-DE', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        {e.user_type === 'flowai' ? (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-400 text-[10px] font-bold">
                            <svg width="10" height="10" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2a2 2 0 012 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 017 7h1a1 1 0 110 2h-1.07A7.001 7.001 0 0113 22h-2a7.001 7.001 0 01-6.93-6H3a1 1 0 110-2h1a7 7 0 017-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 012-2z" /></svg>
                            FlowAI
                          </span>
                        ) : (
                          <span className="text-content">{e.vorname ? `${e.vorname} ${(e.nachname || '')[0] || ''}.` : e.user_email || '–'}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-content">{aktionLabel}</td>
                      <td className="px-3 py-2 text-content-tertiary font-mono">
                        {e.oms_case_id ? `#${e.oms_case_id}` : '–'}
                      </td>
                      <td className="px-3 py-2 text-content-tertiary text-[11px] max-w-[200px] truncate" title={detailStr}>{detailStr || '–'}</td>
                      <td className="px-3 py-2">
                        {e.erfolg ? (
                          <span className="text-green-500">✓</span>
                        ) : (
                          <span className="text-red-500 cursor-help" title={e.fehler_message || ''}>✗</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {omsActivity.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-4 text-center text-content-tertiary">{t('dataHub.keineEintraege')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {omsActivity.length < omsActivityTotal && (
            <div className="text-center mt-3">
              <button
                onClick={() => loadOmsActivity(omsActivityFilter, omsActivityOffset + 50)}
                className="px-3 py-1.5 text-[10px] font-medium rounded bg-surface-inset border border-line text-[var(--text-secondary)] hover:text-content hover:bg-surface-hover transition-colors">
                {t('dataHub.mehr')} ({omsActivity.length}/{omsActivityTotal})
              </button>
            </div>
          )}
        </div>
      </>)}

      {activeTab === 'flowoms' && (<>
      {/* ─── FlowOMS Inbound Sync (#1214, Phase 0) — passiv, kein Outbound ─── */}
        <div className="bg-surface-card border border-line rounded-card shadow-card p-5 mb-4">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-[#338edc]/10 flex items-center justify-center flex-shrink-0">
              <svg width="20" height="20" fill="none" stroke="#338edc" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M4 4h16v6H4zM4 14h16v6H4zM7 7h.01M7 17h.01" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-bold text-[var(--text-primary)]">FlowOMS Inbound Sync</div>
              <div className="text-xs text-[var(--text-tertiary)] mt-0.5">{t('dataHub.flowoms.subtitle')}</div>
            </div>
            {flowoms?.token?.configured ? (
              <span className="text-[10px] font-bold px-2 py-1 rounded bg-green-500/10 text-green-600 dark:text-green-400">{t('dataHub.flowoms.tokenActive')}</span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-1 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400">{t('dataHub.flowoms.tokenMissing')}</span>
            )}
          </div>
        </div>

        {flowomsLoading && !flowoms && (
          <div className="text-xs text-content-tertiary text-center py-6">{t('common.loading')}</div>
        )}

        {flowoms && (<>
        {/* Token-Card */}
        <div className="bg-surface-card border border-line rounded-card shadow-card p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs font-semibold text-content">{t('dataHub.flowoms.tokenSection')}</div>
            <div className="flex gap-2">
              <button onClick={flowomsGenerateToken} disabled={flowomsAction === 'generating'}
                className="px-3 py-1.5 text-[11px] font-medium rounded bg-[#338edc] text-white hover:bg-[#2a78c1] transition-colors disabled:opacity-50">
                {flowomsAction === 'generating'
                  ? t('common.loading')
                  : (flowoms.token.configured ? t('dataHub.flowoms.regenerate') : t('dataHub.flowoms.generate'))}
              </button>
              {flowoms.token.configured && (
                <button onClick={flowomsRevokeToken} disabled={flowomsAction === 'revoking'}
                  className="px-3 py-1.5 text-[11px] font-medium rounded border border-red-500/30 text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-50">
                  {flowomsAction === 'revoking' ? t('common.loading') : t('dataHub.flowoms.revoke')}
                </button>
              )}
            </div>
          </div>
          {flowoms.token.configured ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="bg-surface-inset border border-line rounded-lg p-3">
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.tokenHint')}</div>
                <div className="text-xs font-mono text-content">{flowoms.token.hint || '–'}</div>
              </div>
              <div className="bg-surface-inset border border-line rounded-lg p-3">
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.tokenCreated')}</div>
                <div className="text-xs text-content">{flowoms.token.created_at ? new Date(flowoms.token.created_at).toLocaleString('de-DE') : '–'}</div>
              </div>
              <div className="bg-surface-inset border border-line rounded-lg p-3">
                <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.tokenBy')}</div>
                <div className="text-xs text-content">{flowoms.token.created_by || '–'}</div>
              </div>
            </div>
          ) : (
            <div className="text-xs text-[var(--text-tertiary)]">{t('dataHub.flowoms.noTokenInfo')}</div>
          )}

          {flowomsNewToken && (
            <div className="mt-4 bg-amber-500/5 border border-amber-500/30 rounded-lg p-3">
              <div className="flex items-start gap-2 mb-2">
                <svg width="16" height="16" fill="none" stroke="#f59e0b" strokeWidth="1.5" viewBox="0 0 24 24" className="flex-shrink-0 mt-0.5"><path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <div className="text-[11px] font-semibold text-amber-600 dark:text-amber-400">{t('dataHub.flowoms.tokenShowOnce')}</div>
              </div>
              <div className="bg-surface-elevated border border-line rounded p-2 text-[11px] font-mono break-all text-content select-all">{flowomsNewToken}</div>
              <button onClick={() => { navigator.clipboard?.writeText(flowomsNewToken); }}
                className="mt-2 px-2 py-1 text-[10px] font-medium rounded border border-line text-content hover:bg-surface-hover transition-colors">
                {t('common.copy') || 'Kopieren'}
              </button>
              <button onClick={() => setFlowomsNewToken(null)}
                className="mt-2 ml-2 px-2 py-1 text-[10px] font-medium rounded border border-line text-content-tertiary hover:bg-surface-hover transition-colors">
                {t('common.close') || 'Schließen'}
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <div className="bg-surface-card border border-line rounded-card p-3">
            <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.totalRecords')}</div>
            <div className="text-lg font-bold text-[var(--text-primary)]">{fmt(flowoms.stats.total)}</div>
          </div>
          <div className="bg-surface-card border border-line rounded-card p-3">
            <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.statusPending')}</div>
            <div className="text-lg font-bold text-[var(--text-primary)]">{fmt(flowoms.stats.by_status.pending || 0)}</div>
          </div>
          <div className="bg-surface-card border border-line rounded-card p-3">
            <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.statusConfirmed')}</div>
            <div className="text-lg font-bold text-[var(--text-primary)]">{fmt(flowoms.stats.by_status.confirmed || 0)}</div>
          </div>
          <div className="bg-surface-card border border-line rounded-card p-3">
            <div className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold mb-1">{t('dataHub.flowoms.lastReceived')}</div>
            <div className="text-sm font-bold text-[var(--text-primary)]">{flowoms.stats.last_received_at ? timeAgo(flowoms.stats.last_received_at, t) : '–'}</div>
          </div>
        </div>

        {/* Last Records */}
        <div className="bg-surface-card border border-line rounded-card shadow-card p-4 mb-4">
          <div className="text-xs font-semibold text-content mb-3">{t('dataHub.flowoms.lastRecords')}</div>
          {flowoms.last_records && flowoms.last_records.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead className="text-[10px] text-[var(--text-secondary)] uppercase">
                  <tr className="border-b border-line">
                    <th className="text-left py-2 pr-3">{t('dataHub.flowoms.colReceived')}</th>
                    <th className="text-left py-2 pr-3">{t('dataHub.flowoms.colResource')}</th>
                    <th className="text-left py-2 pr-3">{t('dataHub.flowoms.colCompany')}</th>
                    <th className="text-left py-2 pr-3">{t('dataHub.flowoms.colExternalId')}</th>
                    <th className="text-left py-2 pr-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {flowoms.last_records.map(r => (
                    <tr key={r.id} className="border-b border-line/50">
                      <td className="py-1.5 pr-3 text-content-secondary">{new Date(r.received_at).toLocaleString('de-DE')}</td>
                      <td className="py-1.5 pr-3 font-mono text-content-secondary">{r.resource_type}</td>
                      <td className="py-1.5 pr-3 font-mono text-content-secondary">{r.company_external_id}</td>
                      <td className="py-1.5 pr-3 font-mono text-content-secondary">{r.external_id}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                          r.status === 'confirmed' ? 'bg-green-500/10 text-green-500'
                          : r.status === 'rejected' ? 'bg-red-500/10 text-red-500'
                          : 'bg-amber-500/10 text-amber-500'}`}>{r.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-xs text-[var(--text-tertiary)] py-4 text-center">{t('dataHub.flowoms.noRecords')}</div>
          )}
        </div>

        {/* By Company */}
        {flowoms.stats.by_company && flowoms.stats.by_company.length > 0 && (
          <div className="bg-surface-card border border-line rounded-card shadow-card p-4 mb-4">
            <div className="text-xs font-semibold text-content mb-3">{t('dataHub.flowoms.perCompany')}</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {flowoms.stats.by_company.map(c => (
                <div key={c.company_external_id} className="bg-surface-inset border border-line rounded px-2 py-1.5">
                  <div className="text-[10px] font-mono text-content-secondary truncate">{c.company_external_id}</div>
                  <div className="text-sm font-bold text-[var(--text-primary)]">{fmt(c.count)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Endpoint-Liste für Paul */}
        <div className="bg-surface-card border border-line rounded-card shadow-card p-4 mb-4">
          <div className="text-xs font-semibold text-content mb-3">{t('dataHub.flowoms.endpoints')}</div>
          <div className="space-y-2 text-[11px] font-mono">
            {/* Forecasts (#1252) */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/forecasts</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/forecasts/:external_id/status</span>
              <span className="text-[10px] text-content-tertiary">Bearer</span>
            </div>
            {/* AI-Suggestion + Change-Requests (#1261) */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/forecasts/ai-suggestion</span>
              <span className="text-[10px] text-content-tertiary">Bearer · ?company_external_id=&year=&month=</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/forecast-change-requests</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/forecast-change-requests/:external_id/status</span>
              <span className="text-[10px] text-content-tertiary">Bearer</span>
            </div>
            {/* Promotions (#1262) */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/promotions</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/promotions/:external_id/status</span>
              <span className="text-[10px] text-content-tertiary">Bearer</span>
            </div>
            {/* Claims-Sync (#1264) */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/claims</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/claims/:external_id/documents</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key (max 25 MB)</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/claims/:external_id/messages</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/claims/:external_id/status</span>
              <span className="text-[10px] text-content-tertiary">Bearer</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/claims/:external_id/messages</span>
              <span className="text-[10px] text-content-tertiary">Bearer · ?since=&lt;ISO&gt;</span>
            </div>
            {/* Commitments-Sync (#1266) — v2 ist Master, Flow pullt + updated Status */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/commitments</span>
              <span className="text-[10px] text-content-tertiary">Bearer · ?since=&lt;ISO&gt;</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-500">POST</span>
              <span className="text-content-secondary">/v1/api/flowoms/commitments/:commitment_nr/status</span>
              <span className="text-[10px] text-content-tertiary">Bearer + Idempotency-Key</span>
            </div>
            {/* Health */}
            <div className="flex items-center gap-2">
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-green-500/10 text-green-500">GET</span>
              <span className="text-content-secondary">/v1/api/flowoms/health</span>
              <span className="text-[10px] text-content-tertiary">{t('dataHub.flowoms.noAuth')}</span>
            </div>
          </div>
        </div>
        </>)}
      </>)}

      {activeTab === 'hub' && (<>
      {/* ─── 5. CONNECTIONS (collapsible) ─── */}

      {/* WeShip Odoo */}
      <Section title={`${channelName('weship_odoo', 'WeShip Odoo')} ${t('dataHub.connection')}`} defaultOpen={false}
        icon={<svg width="16" height="16" fill="none" stroke="#f59e0b" strokeWidth="1.5" viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M12 2a3 3 0 00-3 3v1a3 3 0 006 0V5a3 3 0 00-3-3z" /></svg>}>
        <div className="bg-red-500/5 border border-red-500/10 rounded-card p-3 mb-4 flex items-start gap-2">
          <svg width="14" height="14" fill="none" stroke="#ef4444" strokeWidth="1.5" viewBox="0 0 24 24" className="flex-shrink-0 mt-0.5"><path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span className="text-[11px] text-red-400">{t('dataHub.strictReadOnly')}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">URL</label>
            <input type="text" value={odooUrl} onChange={e => setOdooUrl(e.target.value)} placeholder="https://main.weship.at/"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.database')}</label>
            <input type="text" value={odooDb} onChange={e => setOdooDb(e.target.value)} placeholder="weship_main"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">Username</label>
            <input type="text" value={odooUser} onChange={e => setOdooUser(e.target.value)} placeholder="sam-ai@mail.weship.eu"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.password')}</label>
            <div className="relative">
              <input type={showPass.odoo ? 'text' : 'password'} value={odooPass} onChange={e => setOdooPass(e.target.value)}
                placeholder={hasPassword ? '••••••••' : t('dataHub.password')}
                className="w-full px-3 py-2 pr-10 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
              <button type="button" onClick={() => setShowPass(s => ({ ...s, odoo: !s.odoo }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-content p-1"><EyeIcon open={showPass.odoo} /></button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => saveConnection('weship_odoo', { url: odooUrl, db: odooDb, user: odooUser, password: odooPass || undefined })}
            disabled={saving.weship_odoo}
            className="px-4 py-2 bg-surface-inset border border-line-hover rounded-button text-sm font-medium text-content-secondary hover:bg-surface-active transition-colors disabled:opacity-40">
            {saving.weship_odoo ? t('common.saving') : t('common.save')}
          </button>
          <button onClick={() => testConnection('weship_odoo')} disabled={testing.weship_odoo}
            className="px-4 py-2 bg-amber-600/20 border border-amber-500/30 rounded-button text-sm font-medium text-amber-300 hover:bg-amber-600/30 transition-colors disabled:opacity-40">
            {testing.weship_odoo ? t('dataHub.testing') : t('dataHub.testConnection')}
          </button>
          {saveMsg.weship_odoo && <span className={`text-xs ${saveMsg.weship_odoo.ok ? 'text-green-400' : 'text-red-400'}`}>{saveMsg.weship_odoo.text}</span>}
        </div>
      </Section>

      {/* OMS Odoo — read-only, managed in Cases Config */}
      <Section title={`${channelName('oms_odoo', 'OMS Helpdesk')} ${t('dataHub.connection')}`} defaultOpen={false}
        icon={<svg width="16" height="16" fill="none" stroke="#e74c3c" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" /><rect x="9" y="3" width="6" height="4" rx="1" /></svg>}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">READ-ONLY</span>
        </div>
        <div className="space-y-2 mb-4">
          <InfoRow label="URL" value={omsUrl || '–'} />
          <InfoRow label={t('dataHub.database')} value={omsDb || '–'} />
          <InfoRow label="Username" value={omsUser || '–'} />
          <InfoRow label={t('dataHub.password')} value={hasOmsPass ? '••••••••' : '–'} />
        </div>
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-lg bg-primary-500/10 border border-primary-500/20">
          <AlertTriangle className="w-4 h-4 text-primary-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-xs text-primary-300 mb-2">{t('dataHub.omsConfigMovedDesc')}</p>
            <button onClick={() => navigate('/cases/konfiguration')}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-colors">
              {t('dataHub.omsConfigMovedLink')}
            </button>
          </div>
        </div>
      </Section>

      {/* Legacy Dev */}
      <Section title={`${channelName('legacy_dev', 'Performance API')} ${t('dataHub.connection')}`} defaultOpen={false}
        icon={<svg width="16" height="16" fill="none" stroke="#4D9CE0" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" /></svg>}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">READ-ONLY</span>
          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">{t('dataHub.devServerMigrating')}</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">URL</label>
            <input type="text" value={legUrl} onChange={e => setLegUrl(e.target.value)} placeholder="https://legacy.development.dynaflow.at"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.database')}</label>
            <input type="text" value={legDb} onChange={e => setLegDb(e.target.value)} placeholder="weship_development"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">UID</label>
            <input type="text" value={legUid} onChange={e => setLegUid(e.target.value)} placeholder="702"
              className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.password')}</label>
            <div className="relative">
              <input type={showPass.leg ? 'text' : 'password'} value={legPass} onChange={e => setLegPass(e.target.value)}
                placeholder={hasLegPass ? '••••••••' : t('dataHub.password')}
                className="w-full px-3 py-2 pr-10 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
              <button type="button" onClick={() => setShowPass(s => ({ ...s, leg: !s.leg }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-secondary)] hover:text-content p-1"><EyeIcon open={showPass.leg} /></button>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => saveConnection('legacy_dev', { url: legUrl, db: legDb, uid: legUid, password: legPass || undefined })}
            disabled={saving.legacy_dev}
            className="px-4 py-2 bg-surface-inset border border-line-hover rounded-button text-sm font-medium text-content-secondary hover:bg-surface-active transition-colors disabled:opacity-40">
            {saving.legacy_dev ? t('common.saving') : t('common.save')}
          </button>
          <button onClick={() => testConnection('legacy_dev')} disabled={testing.legacy_dev}
            className="px-4 py-2 bg-purple-600/20 border border-purple-500/30 rounded-button text-sm font-medium text-purple-300 hover:bg-purple-600/30 transition-colors disabled:opacity-40">
            {testing.legacy_dev ? t('dataHub.testing') : t('dataHub.testConnection')}
          </button>
          {saveMsg.legacy_dev && <span className={`text-xs ${saveMsg.legacy_dev.ok ? 'text-green-400' : 'text-red-400'}`}>{saveMsg.legacy_dev.text}</span>}
        </div>
      </Section>

      {/* SMTP */}
      <Section title="E-Mail (SMTP)" defaultOpen={false}
        icon={<svg width="16" height="16" fill="none" stroke="#a855f7" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">SMTP Host</label>
            <input type="text" value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="smtp.example.com" className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">Port</label>
            <input type="text" value={smtpPort} onChange={e => setSmtpPort(e.target.value)} placeholder="587" className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">Username</label>
            <input type="text" value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="noreply@weship.eu" className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.password')}</label>
            <input type="password" value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder={hasSmtpPass ? '••••••••' : t('dataHub.password')} className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.senderAddress')}</label>
            <input type="email" value={smtpFrom} onChange={e => setSmtpFrom(e.target.value)} placeholder="noreply@weship.eu" className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
          <div>
            <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">{t('dataHub.senderName')}</label>
            <input type="text" value={smtpFromName} onChange={e => setSmtpFromName(e.target.value)} placeholder="WeShip Performance" className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => {
            setSaving(s => ({ ...s, smtp: true })); setSaveMsg(s => ({ ...s, smtp: null }));
            api('/api/system/smtp', { method: 'POST', body: { host: smtpHost, port: smtpPort, user: smtpUser, pass: smtpPass || undefined, from_address: smtpFrom, from_name: smtpFromName } })
              .then(r => setSaveMsg(s => ({ ...s, smtp: { ok: true, text: r.message } })))
              .catch(e => setSaveMsg(s => ({ ...s, smtp: { ok: false, text: e.message } })))
              .finally(() => setSaving(s => ({ ...s, smtp: false })));
          }} disabled={saving.smtp}
            className="px-4 py-2 bg-surface-inset border border-line-hover rounded-button text-sm font-medium text-content-secondary hover:bg-surface-active transition-colors disabled:opacity-40">
            {saving.smtp ? t('common.saving') : t('common.save')}
          </button>
          {saveMsg.smtp && <span className={`text-xs ${saveMsg.smtp.ok ? 'text-green-400' : 'text-red-400'}`}>{saveMsg.smtp.text}</span>}
        </div>
      </Section>

      {/* Anthropic */}
      <Section title="Anthropic API (WeShip Copilot)" defaultOpen={false}
        icon={<svg width="16" height="16" fill="none" stroke="#338edc" strokeWidth="1.5" viewBox="0 0 24 24"><path d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>}>
        <div className="flex items-center gap-2 mb-3">
          {anthropicStatus && (
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${anthropicStatus.configured ? 'bg-green-500' : 'bg-gray-500'}`} />
              <span className="text-xs text-content-tertiary">
                {anthropicStatus.configured ? `${t('dataHub.configured')} (${anthropicStatus.source === 'system_config' ? 'DB' : 'ENV'})` : t('dataHub.notConfigured')}
              </span>
            </div>
          )}
        </div>
        <div className="mb-3">
          <label className="text-[10px] text-[var(--text-secondary)] uppercase font-semibold block mb-1">API Key</label>
          <input type="password" value={anthropicKey} onChange={e => setAnthropicKey(e.target.value)}
            placeholder={anthropicStatus?.configured ? '••••••••••••••' : 'sk-ant-...'}
            className="w-full px-3 py-2 bg-surface-inset border border-line-hover rounded-lg text-sm text-content placeholder-content-dim focus:outline-none font-mono" />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => {
            if (!anthropicKey.trim()) return;
            setSaving(s => ({ ...s, anthropic: true })); setSaveMsg(s => ({ ...s, anthropic: null }));
            api('/api/system/anthropic', { method: 'POST', body: { api_key: anthropicKey.trim() } })
              .then(r => { setSaveMsg(s => ({ ...s, anthropic: { ok: true, text: r.message } })); setAnthropicStatus({ configured: true, source: 'system_config' }); setAnthropicKey(''); })
              .catch(e => setSaveMsg(s => ({ ...s, anthropic: { ok: false, text: e.message } })))
              .finally(() => setSaving(s => ({ ...s, anthropic: false })));
          }} disabled={saving.anthropic || !anthropicKey.trim()}
            className="px-4 py-2 bg-primary-600/20 border border-primary-500/30 rounded-button text-sm font-medium text-primary-300 hover:bg-primary-600/30 transition-colors disabled:opacity-40">
            {saving.anthropic ? t('common.saving') : t('common.save')}
          </button>
          {saveMsg.anthropic && <span className={`text-xs ${saveMsg.anthropic.ok ? 'text-green-400' : 'text-red-400'}`}>{saveMsg.anthropic.text}</span>}
        </div>
      </Section>

      {/* ─── 6. AUTOMATISIERUNG (Cronjobs) ─── */}
      <div className="bg-surface-card border border-line rounded-card p-5 shadow-card mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
            <Settings className="w-4 h-4 text-primary-400" />
          </div>
          <div>
            <div className="text-sm font-semibold text-content">{t('dataHub.automaticJobs')}</div>
            <div className="text-[10px] text-content-tertiary">{t('dataHub.automaticJobsDesc')}</div>
          </div>
        </div>

        {cronjobs.length === 0 ? (
          <div className="text-xs text-content-tertiary italic py-4 text-center">{t('dataHub.noCronjobs')}</div>
        ) : (
          <div className="space-y-4">
            {cronjobs.map(job => {
              const isRunning = job.running || cronjobRunning[job.name];
              const lastOk = job.last_status === 'success';
              const lastErr = job.last_status && job.last_status.startsWith('error');

              return (
                <div key={job.name} className="border border-line rounded-card p-4">
                  {/* Header Row */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${isRunning ? 'bg-[#338edc] animate-pulse' : job.enabled ? 'bg-green-500' : 'bg-gray-500'}`} />
                        <span className="text-sm font-semibold text-content">{job.label}</span>
                      </div>
                    </div>
                    {/* Toggle */}
                    <ToggleSwitch size="sm" checked={job.enabled} onChange={async (newEnabled) => {
                        try {
                          await api(`/api/admin/cronjobs/${job.name}`, {
                            method: 'PUT',
                            body: { enabled: newEnabled },
                          });
                          setCronjobs(prev => prev.map(j => j.name === job.name ? { ...j, enabled: newEnabled } : j));
                        } catch (e) { console.error(e); }
                      }} />
                  </div>

                  {/* Schedule */}
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-[10px] text-content-tertiary uppercase font-semibold w-14">{t('dataHub.schedule')}</span>
                    {(() => {
                      const SCHEDULE_OPTIONS = [
                        { value: '*/5 * * * *', label: t('dataHub.every5min') },
                        { value: '*/15 * * * *', label: t('dataHub.every15min') },
                        { value: '0 0 * * *', label: t('dataHub.daily00') },
                        { value: '0 3 * * *', label: t('dataHub.daily03') },
                        { value: '0 5 * * *', label: t('dataHub.daily05') },
                        { value: '0 6 * * *', label: t('dataHub.daily06') },
                        { value: '30 6 * * *', label: t('dataHub.daily0630') },
                        { value: '0 7 * * *', label: t('dataHub.daily07') },
                        { value: '0 8 * * *', label: t('dataHub.daily08') },
                        { value: '0 12 * * *', label: t('dataHub.daily12') },
                        { value: '0 18 * * *', label: t('dataHub.daily18') },
                        { value: '0 23 * * *', label: t('dataHub.daily23') },
                        { value: '0 */2 * * *', label: t('dataHub.every2h') },
                        { value: '0 */4 * * *', label: t('dataHub.every4h') },
                        { value: '0 7 1 * *', label: t('dataHub.monthly1st07') },
                      ];
                      const knownValues = SCHEDULE_OPTIONS.map(o => o.value);
                      const isKnown = knownValues.includes(job.schedule);
                      return (
                        <select
                          value={isKnown ? job.schedule : 'custom'}
                          onChange={async (e) => {
                            if (e.target.value === 'custom') return;
                            try {
                              await api(`/api/admin/cronjobs/${job.name}`, {
                                method: 'PUT',
                                body: { schedule: e.target.value },
                              });
                              setCronjobs(prev => prev.map(j => j.name === job.name ? { ...j, schedule: e.target.value, schedule_human: '' } : j));
                              api('/api/admin/cronjobs').then(d => { if (Array.isArray(d)) setCronjobs(d); }).catch(() => {});
                            } catch (err) { console.error(err); }
                          }}
                          className="bg-surface-inset border border-line rounded px-2 py-1 text-xs text-content flex-1 max-w-[220px]"
                        >
                          {SCHEDULE_OPTIONS.map(o => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                          {!isKnown && (
                            <option value="custom">{t('dataHub.custom')}: {job.schedule}</option>
                          )}
                        </select>
                      );
                    })()}
                    <span className="text-[10px] text-content-tertiary">{job.schedule_human}</span>
                  </div>

                  {/* Status Row */}
                  <div className="flex items-center gap-3 mb-3 text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-content-tertiary">{t('dataHub.lastRun')}:</span>
                      {job.last_run ? (
                        <>
                          <span className="text-content-secondary">
                            {new Date(job.last_run).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </span>
                          {lastOk && <CheckCircle className="w-3 h-3 text-green-400" />}
                          {lastErr && <XCircle className="w-3 h-3 text-red-400" />}
                          {job.last_duration_ms != null && (
                            <span className="text-content-tertiary">({(job.last_duration_ms / 1000).toFixed(1)}s)</span>
                          )}
                        </>
                      ) : (
                        <span className="text-content-tertiary italic">{t('dataHub.neverRun')}</span>
                      )}
                    </div>
                    {job.next_run && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-content-tertiary">{t('dataHub.next')}:</span>
                        <span className="text-content-secondary">
                          {new Date(job.next_run).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Error message */}
                  {lastErr && (
                    <div className="mb-3 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-card px-3 py-2 truncate">
                      {job.last_status}
                    </div>
                  )}

                  {/* Progress (when running) */}
                  {isRunning && job.progress && (
                    <div className="mb-3">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="animate-spin h-3 w-3 border-2 border-[#338edc] border-t-transparent rounded-full" />
                        <span className="text-xs text-primary-400">{job.progress.message || t('dataHub.running')}</span>
                      </div>
                      {job.progress.percent > 0 && (
                        <div className="w-full bg-surface-inset rounded-full h-1.5">
                          <div className="bg-[#338edc] h-1.5 rounded-full transition-all" style={{ width: `${job.progress.percent}%` }} />
                        </div>
                      )}
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex items-center gap-2">
                    <button
                      disabled={isRunning}
                      onClick={async () => {
                        setCronjobRunning(r => ({ ...r, [job.name]: true }));
                        try {
                          await api(`/api/admin/cronjobs/${job.name}/run`, { method: 'POST', body: {} });
                          // Start polling for status
                          const iv = setInterval(async () => {
                            try {
                              const st = await api(`/api/admin/cronjobs/${job.name}/status`);
                              if (st.running && st.progress) {
                                setCronjobs(prev => prev.map(j => j.name === job.name ? { ...j, running: true, progress: st.progress } : j));
                              } else {
                                clearInterval(iv);
                                setCronjobPolling(null);
                                setCronjobRunning(r => ({ ...r, [job.name]: false }));
                                // Reload cronjobs
                                api('/api/admin/cronjobs').then(d => { if (Array.isArray(d)) setCronjobs(d); }).catch(() => {});
                              }
                            } catch {
                              clearInterval(iv);
                              setCronjobPolling(null);
                              setCronjobRunning(r => ({ ...r, [job.name]: false }));
                              api('/api/admin/cronjobs').then(d => { if (Array.isArray(d)) setCronjobs(d); }).catch(() => {});
                            }
                          }, 2000);
                          setCronjobPolling(iv);
                        } catch (e) {
                          setCronjobRunning(r => ({ ...r, [job.name]: false }));
                          console.error(e);
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-primary-600/10 hover:bg-primary-600/20 text-primary-400 rounded-button text-xs font-medium transition-colors disabled:opacity-40"
                    >
                      {isRunning ? (
                        <div className="animate-spin h-3 w-3 border-2 border-primary-400 border-t-transparent rounded-full" />
                      ) : (
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>
                      )}
                      {isRunning ? t('dataHub.running') : t('dataHub.runNow')}
                    </button>
                    <button
                      disabled={isRunning}
                      onClick={async () => {
                        if (!confirm(t('dataHub.confirmFullRecalc'))) return;
                        setCronjobRunning(r => ({ ...r, [job.name]: true }));
                        try {
                          await api(`/api/admin/cronjobs/${job.name}/run`, { method: 'POST', body: { force_recalc: true } });
                          const iv = setInterval(async () => {
                            try {
                              const st = await api(`/api/admin/cronjobs/${job.name}/status`);
                              if (st.running && st.progress) {
                                setCronjobs(prev => prev.map(j => j.name === job.name ? { ...j, running: true, progress: st.progress } : j));
                              } else {
                                clearInterval(iv);
                                setCronjobRunning(r => ({ ...r, [job.name]: false }));
                                api('/api/admin/cronjobs').then(d => { if (Array.isArray(d)) setCronjobs(d); }).catch(() => {});
                              }
                            } catch {
                              clearInterval(iv);
                              setCronjobRunning(r => ({ ...r, [job.name]: false }));
                              api('/api/admin/cronjobs').then(d => { if (Array.isArray(d)) setCronjobs(d); }).catch(() => {});
                            }
                          }, 2000);
                        } catch (e) {
                          setCronjobRunning(r => ({ ...r, [job.name]: false }));
                          console.error(e);
                        }
                      }}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600/10 hover:bg-amber-600/20 text-amber-400 rounded-button text-xs font-medium transition-colors disabled:opacity-40"
                    >
                      <RefreshCw className="w-3 h-3" />
                      {t('dataHub.fullRecalc')}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ─── 7. SYSTEM-INFO + DB TABLES ─── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-pink-500/10 flex items-center justify-center">
              <svg width="16" height="16" fill="none" stroke="#ec4899" strokeWidth="1.5" viewBox="0 0 24 24"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8M12 17v4" strokeLinecap="round" /></svg>
            </div>
            <div className="text-sm font-semibold text-content">{t('dataHub.systemInfo')}</div>
          </div>
          <div>
            <InfoRow label="Node.js" value={si.node_version || '–'} mono />
            <InfoRow label={t('dataHub.apiVersion')} value={si.api_version || '–'} mono />
            <InfoRow label={t('dataHub.platform')} value={si.platform || '–'} mono />
            <InfoRow label="PID" value={si.pid || '–'} mono />
            <InfoRow label="Uptime" value={si.uptime_human || '–'} mono />
            <InfoRow label="RAM (RSS)" value={si.ram_used_mb ? `${si.ram_used_mb} MB` : '–'} mono />
            <InfoRow label="RAM (Heap)" value={si.ram_heap_mb ? `${si.ram_heap_mb} MB` : '–'} mono />
          </div>
        </div>
        <div className="bg-surface-card border border-line rounded-card p-5 shadow-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-lg bg-primary-500/10 flex items-center justify-center">
              <svg width="16" height="16" fill="none" stroke="#338edc" strokeWidth="1.5" viewBox="0 0 24 24"><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" /><path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" /></svg>
            </div>
            <div className="text-sm font-semibold text-content">{t('dataHub.databaseTables')}</div>
          </div>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {pg.table_details?.map(t => {
              const count = pg.row_counts?.[t.name];
              return (
                <div key={t.name} className="flex items-center justify-between py-1.5 border-b border-line">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-content-secondary">{t.name}</span>
                    {count != null && <span className="text-[10px] text-content-tertiary">{count.toLocaleString('de-DE')} rows</span>}
                  </div>
                  <span className="text-[10px] font-mono text-[var(--text-secondary)]">{t.size}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      </>)}

      {/* Modals */}
      {importModal && <ImportModal job={importModal} onClose={() => { setImportModal(null); loadAll(); }} t={t} />}
      {configModal && <ConfigModal job={configModal} onClose={() => setConfigModal(null)} t={t} />}
    </div>
  );
}
